/* eslint-disable @typescript-eslint/no-restricted-types */
/* eslint-disable no-console */
import { useOktaAuth } from '@okta/okta-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionManager } from './security-utils';

export interface LoginCallbackProps {
  /**
   * Custom loading component to show during authentication
   */
  loadingComponent?: React.ComponentType;

  /**
   * Custom error component to show on authentication failure
   */
  errorComponent?: React.ComponentType<{ error: string; onRetry: () => void }>;

  /**
   * Callback fired on successful authentication
   */
  onSuccess?: (tokens: { accessToken?: string; idToken?: string }) => void;

  /**
   * Callback fired on authentication error
   */
  onError?: (error: Error) => void;

  /**
   * Enable debug logging
   */
  enableDebugLogs?: boolean;

  /**
   * Enable additional security validations
   * @default false - Set to true only if you need strict security validation
   */
  enableSecurityValidation?: boolean;
}

interface AuthState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Enhanced LoginCallback component that handles the OAuth callback flow
 * with additional security validations and customization options
 */
export const LoginCallback: React.FC<LoginCallbackProps> = ({
  loadingComponent: LoadingComponent,
  errorComponent: ErrorComponent,
  onSuccess,
  onError,
  enableDebugLogs = false,
  enableSecurityValidation = false,
}) => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    loading: true,
    error: null,
    success: false,
  });

  const handleRetry = () => {
    setAuthState({ loading: true, error: null, success: false });
    handleCallback();
  };

  const handleCallback = async () => {
    try {
      if (enableDebugLogs) {
        console.log('LoginCallback: Starting OAuth callback handling');
      }

      // Check if this is actually a callback URL
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthParams =
        urlParams.has('code') || urlParams.has('error') || urlParams.has('state');

      if (hasAuthParams) {
        try {
          await oktaAuth.handleLoginRedirect();
          if (enableDebugLogs) {
            console.log('LoginCallback: OAuth callback handled successfully');
          }
        } catch (callbackError) {
          // Check if we're already logged in (tokens exist)
          const existingTokens = await Promise.all([
            oktaAuth.tokenManager.get('accessToken').catch(() => null),
            oktaAuth.tokenManager.get('idToken').catch(() => null),
          ]);

          if (existingTokens[0] || existingTokens[1]) {
            // eslint-disable-next-line max-depth
            if (enableDebugLogs) {
              console.log('LoginCallback: User already authenticated, using existing tokens');
            }
          } else {
            throw callbackError;
          }
        }
      } else {
        // Handle the OAuth callbac

        if (enableDebugLogs) {
          console.log('LoginCallback: No OAuth parameters found, checking existing authentication');
        }

        // Check if user is already authenticated
        const isAuthenticated = await oktaAuth.isAuthenticated();
        if (!isAuthenticated) {
          throw new Error('No authentication data found. Please log in again.');
        }
      }

      // Get tokens for validation and success callback
      const [accessToken, idToken] = await Promise.all([
        oktaAuth.tokenManager.get('accessToken').catch(() => null),
        oktaAuth.tokenManager.get('idToken').catch(() => null),
      ]);

      if (enableDebugLogs) {
        console.log('LoginCallback: Tokens retrieved', {
          hasAccessToken: Boolean(accessToken),
          hasIdToken: Boolean(idToken),
        });
      }

      // Check if we have at least one token
      if (!accessToken && !idToken) {
        throw new Error('No authentication tokens found');
      }

      // Skip client-side validation - not effective in SPAs
      if (enableSecurityValidation && enableDebugLogs) {
        console.log(
          'LoginCallback: Client-side validation disabled - let your API handle token validation',
        );
      }

      // Create simple session for timeout tracking
      const sessionId = crypto.randomUUID();
      sessionManager.createSession(sessionId);

      if (enableDebugLogs) {
        console.log('LoginCallback: Session created for timeout tracking', {
          sessionId,
        });
      }

      // Success callback
      if (onSuccess) {
        onSuccess({
          accessToken: accessToken
            ? (accessToken as { accessToken: string }).accessToken
            : undefined,
          idToken: idToken ? (idToken as { idToken: string }).idToken : undefined,
        });
      }

      setAuthState({ loading: false, error: null, success: true });

      // Only perform a manual navigate if restoreOriginalUri was NOT executed.
      // When handleLoginRedirect succeeds, restoreOriginalUri already positioned the app.
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';

      if (enableDebugLogs) {
        console.error('LoginCallback: Authentication failed', error);
      }

      setAuthState({ loading: false, error: errorMessage, success: false });

      if (onError) {
        onError(error instanceof Error ? error : new Error(errorMessage));
      }
    }
  };

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show custom loading component
  if (authState.loading && LoadingComponent) {
    return <LoadingComponent />;
  }

  // Show custom error component
  if (authState.error && ErrorComponent) {
    return <ErrorComponent error={authState.error} onRetry={handleRetry} />;
  }

  // Default loading state
  if (authState.loading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-background'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4'></div>
          <h2 className='text-xl font-semibold text-foreground mb-2'>Completing Sign In</h2>
          <p className='text-muted-foreground'>Please wait while we securely log you in...</p>
        </div>
      </div>
    );
  }

  // Default error state
  if (authState.error) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-background'>
        <div className='text-center max-w-md mx-auto px-4'>
          <div className='text-destructive mb-4'>
            <svg
              className='w-16 h-16 mx-auto'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.88-.833-2.65 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-foreground mb-2'>Authentication Failed</h2>
          <p className='text-muted-foreground mb-6'>{authState.error}</p>
          <div className='space-y-3'>
            <button
              onClick={handleRetry}
              className='w-full bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors'
            >
              Try Again
            </button>
            <button
              onClick={() => {
                navigate('/login', { replace: true });
              }}
              className='w-full bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors'
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state (shouldn't be visible long due to redirect)
  if (authState.success) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-background'>
        <div className='text-center'>
          <div className='text-accent mb-4'>
            <svg
              className='w-16 h-16 mx-auto'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-foreground mb-2'>Sign In Successful</h2>
          <p className='text-muted-foreground'>Redirecting you now...</p>
        </div>
      </div>
    );
  }

  return null;
};
